import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CardField } from "@/components/RecordCard";
import {
  downloadGlQueryXlsx,
  type GlQueryResult,
  getGlQuery,
  type ReportBasis,
} from "@/lib/api";
import { ReportBackButton } from "./ReportBackButton";
import { ReportError } from "./StatementTables";

// GL-query page for GET /finance/invoicing/reports/gl-query (JSON
// envelope over `aggregate_by_date_range` — all amounts are integer minor
// units — cents — never floats). Reimplemented for this app; the upstream
// dubbl GL pages are layout reference only and must not be copied (see
// docs/legal/upstream-frontends.md).
//
// NOTE: the handler hardcodes `account_types: None` in its GLQueryOptions
// (see `gl_query_json` in
// backend/service/src/controllers/crate_surfaces.rs), so there
// is no server-side account-type query param — the type filter below applies
// client-side to the returned rows. If the backend ever accepts one, send it
// instead and drop the client filter.

// The JSON envelope carries no currency field (the sibling .xlsx handler
// defaults to USD the same way), so display in the organization default.
const DISPLAY_CURRENCY = "USD";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

interface GlQueryForm {
  start: string;
  end: string;
  basis: ReportBasis;
  includeEmpty: boolean;
}

export function GlQueryPage() {
  const [start, setStart] = useState(firstOfMonthIso);
  const [end, setEnd] = useState(todayIso);
  const [basis, setBasis] = useState<ReportBasis>("accrual");
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [applied, setApplied] = useState<GlQueryForm>({
    start: firstOfMonthIso(),
    end: todayIso(),
    basis: "accrual",
    includeEmpty: false,
  });
  const [result, setResult] = useState<GlQueryResult | null>(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const run = useCallback((query: GlQueryForm) => {
    setLoading(true);
    setError(null);
    getGlQuery({
      start_date: query.start,
      end_date: query.end,
      basis: query.basis,
      include_empty_accounts: query.includeEmpty,
    })
      .then((next) => {
        setResult(next);
        setTypeFilter("all");
      })
      .catch((cause: unknown) => {
        // A failed read is its own state: drop the loaded rows instead of
        // showing stale (or zeroed) totals, and surface the error with Retry.
        setResult(null);
        setError(toErrorMessage(cause));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end, basis, includeEmpty });
  };

  // Distinct account types present in this result drive the client-side
  // filter (see the header note — the endpoint takes no type param).
  const accountTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const account of result?.accounts ?? [])
      seen.add(account.account_type);
    return [...seen].sort();
  }, [result]);

  const visible = useMemo(() => {
    if (!result) return [];
    if (typeFilter === "all") return result.accounts;
    return result.accounts.filter(
      (account) => account.account_type === typeFilter,
    );
  }, [result, typeFilter]);

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    let balance = 0;
    for (const account of visible) {
      debit += account.debit_cents;
      credit += account.credit_cents;
      balance += account.balance_cents;
    }
    return { debit, credit, balance };
  }, [visible]);

  const exportXlsx = async () => {
    setExporting(true);
    setError(null);
    try {
      await downloadGlQueryXlsx({
        start_date: applied.start,
        end_date: applied.end,
        basis: applied.basis,
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div>
        <PageHeader
          title="GL query"
          description="Account-level debit, credit, and balance aggregates for a date range — the same aggregation the GL workbook export uses."
          actions={<ReportBackButton />}
        />
        {result ? (
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {result.start_date} – {result.end_date} · {result.basis} ·{" "}
            {DISPLAY_CURRENCY}
          </p>
        ) : null}
      </div>

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <form
          onSubmit={submit}
          className="surface flex min-w-0 flex-1 flex-wrap items-end gap-3 rounded-2xl p-5"
        >
          <label htmlFor="gl-start" className="grid gap-1 text-xs font-medium">
            Start date
            <Input
              id="gl-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <label htmlFor="gl-end" className="grid gap-1 text-xs font-medium">
            End date
            <Input
              id="gl-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <label htmlFor="gl-basis" className="grid gap-1 text-xs font-medium">
            Basis
            <select
              id="gl-basis"
              className="select"
              value={basis}
              onChange={(e) => setBasis(e.target.value as ReportBasis)}
              disabled={loading}
            >
              <option value="accrual">Accrual</option>
              <option value="cash">Cash</option>
            </select>
          </label>
          <label
            htmlFor="gl-include-empty"
            className="flex items-center gap-2 text-xs font-medium"
          >
            <input
              id="gl-include-empty"
              type="checkbox"
              checked={includeEmpty}
              onChange={(e) => setIncludeEmpty(e.target.checked)}
              disabled={loading}
            />
            Include empty accounts
          </label>
          <Button type="submit" disabled={loading || !start || !end}>
            {loading ? "Running..." : "Run query"}
          </Button>
        </form>
        <span className="ms-auto">
          <Button
            type="button"
            variant="secondary"
            onClick={exportXlsx}
            disabled={exporting || loading || result === null}
            title="Download the GL-query workbook (.xlsx) for the applied range"
          >
            {exporting ? "Exporting..." : "Export (XLSX)"}
          </Button>
        </span>
      </div>

      {loading && result === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : result ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <label
              htmlFor="gl-type-filter"
              className="flex items-center gap-2 text-xs font-medium"
            >
              Account type
              <select
                id="gl-type-filter"
                className="select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All types</option>
                {accountTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-[var(--muted-foreground)]">
              Showing {visible.length} of {result.accounts.length} loaded{" "}
              {result.accounts.length === 1 ? "account" : "accounts"}. The
              account-type filter applies to the loaded result for the applied
              period, and the totals below cover the filtered rows only.
            </p>
          </div>

          <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        {result.accounts.length === 0
                          ? "No account activity in this period."
                          : "No accounts match this type filter."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    visible.map((account) => (
                      <TableRow key={account.account_id}>
                        <TableCell className="font-mono text-xs text-[var(--muted-foreground)]">
                          {account.code}
                        </TableCell>
                        <TableCell>
                          <Link
                            to="/journal"
                            search={{
                              account_id: account.account_id,
                              start_date: applied.start,
                              end_date: applied.end,
                            }}
                            title="Open this account's postings in the ledger"
                            className="underline decoration-dotted underline-offset-4 hover:text-[var(--primary)]"
                          >
                            {account.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {account.account_type}
                          {account.sub_type ? (
                            <span className="block text-xs text-[var(--muted-foreground)]">
                              {account.sub_type}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(account.debit_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(account.credit_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(account.balance_cents)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {visible.length > 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="font-semibold">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {money(totals.debit)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {money(totals.credit)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {money(totals.balance)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="stack-list grid gap-3 min-[64rem]:hidden">
            {visible.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                {result.accounts.length === 0
                  ? "No account activity in this period."
                  : "No accounts match this type filter."}
              </p>
            ) : (
              <>
                {visible.map((account) => (
                  <article
                    key={account.account_id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/journal"
                        search={{
                          account_id: account.account_id,
                          start_date: applied.start,
                          end_date: applied.end,
                        }}
                        title="Open this account's postings in the ledger"
                        className="font-medium underline decoration-dotted underline-offset-4 hover:text-[var(--primary)]"
                      >
                        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                          {account.code}
                        </span>
                        {account.name}
                      </Link>
                      <span className="tabular-nums">
                        {money(account.balance_cents)}
                      </span>
                    </div>
                    <CardField label="Type">
                      {account.account_type}
                      {account.sub_type ? (
                        <span className="block text-xs text-[var(--muted-foreground)]">
                          {account.sub_type}
                        </span>
                      ) : null}
                    </CardField>
                    <CardField label="Debit">
                      {money(account.debit_cents)}
                    </CardField>
                    <CardField label="Credit">
                      {money(account.credit_cents)}
                    </CardField>
                    <CardField label="Balance">
                      {money(account.balance_cents)}
                    </CardField>
                  </article>
                ))}
                <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
                  <p className="text-sm font-semibold">
                    Totals for the filtered rows
                  </p>
                  <CardField label="Debit">{money(totals.debit)}</CardField>
                  <CardField label="Credit">{money(totals.credit)}</CardField>
                  <CardField label="Balance">{money(totals.balance)}</CardField>
                </article>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
